import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const clean = (v) => v.replace(/\D/g, '').slice(0, 6);

const PinField = ({ id, label, hint, value, onChange }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
    <input
      id={id}
      type="password"
      inputMode="numeric"
      autoComplete="off"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(clean(e.target.value))}
      placeholder="••••••"
      className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-slate-900 tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
      data-testid={`${id}-input`}
    />
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const AdminAccessCodes = () => {
  const navigate = useNavigate();
  const { getAuthHeader, logout, isViewer } = useAuth();
  const [f, setF] = useState({ current: '', admin: '', admin2: '', viewer: '', viewer2: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  if (isViewer()) return <Navigate to="/admin/vehicles" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (f.current.length !== 6) return toast.error('Entrez votre code admin actuel (6 chiffres)');
    if (!f.admin && !f.viewer) return toast.error('Entrez au moins un nouveau code');
    for (const [a, b, name] of [[f.admin, f.admin2, 'admin'], [f.viewer, f.viewer2, 'équipe']]) {
      if (a && a.length !== 6) return toast.error(`Le nouveau code ${name} doit avoir 6 chiffres`);
      if (a !== b) return toast.error(`La confirmation du code ${name} ne correspond pas`);
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/auth/change-pin`, {
        current_admin_pin: f.current,
        new_admin_pin: f.admin || null,
        new_viewer_pin: f.viewer || null,
      }, { headers: getAuthHeader() });
      toast.success('Code(s) modifié(s). Tout le monde a été déconnecté — reconnectez-vous avec le nouveau code.');
      logout();
      navigate('/login');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors du changement de code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <form onSubmit={submit} className="max-w-xl space-y-8" data-testid="admin-access-codes">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><KeyRound className="h-6 w-6 text-blue-600" /> Codes d'accès</h1>
          <p className="text-sm text-slate-500 mt-1">Changez le code admin, le code équipe, ou les deux. Après le changement, tous les appareils connectés seront déconnectés.</p>
        </div>

        <section className="space-y-4 bg-white border border-slate-200 rounded-xl p-5">
          <PinField id="current-admin-pin" label="Code admin actuel" value={f.current} onChange={set('current')} />
        </section>

        <section className="space-y-4 bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-semibold text-slate-900">Nouveau code admin <span className="text-slate-400 font-normal text-sm">(accès complet)</span></h2>
          <PinField id="new-admin-pin" label="Nouveau code" hint="Laissez vide pour ne pas le changer" value={f.admin} onChange={set('admin')} />
          <PinField id="confirm-admin-pin" label="Confirmer le nouveau code" value={f.admin2} onChange={set('admin2')} />
        </section>

        <section className="space-y-4 bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-semibold text-slate-900">Nouveau code équipe <span className="text-slate-400 font-normal text-sm">(lecture seule, étiquettes)</span></h2>
          <PinField id="new-viewer-pin" label="Nouveau code" hint="Laissez vide pour ne pas le changer" value={f.viewer} onChange={set('viewer')} />
          <PinField id="confirm-viewer-pin" label="Confirmer le nouveau code" value={f.viewer2} onChange={set('viewer2')} />
        </section>

        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60" data-testid="change-pin-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Enregistrer et déconnecter tout le monde
        </button>
      </form>
    </AdminLayout>
  );
};

export default AdminAccessCodes;
